const FRONT_CHAT_SHOW_RETRY_MS = 250;
const FRONT_CHAT_SHOW_MAX_ATTEMPTS = 40;

let frontChatInitCompleted = false;
const frontChatInitWaiters: Array<() => void> = [];

/** Marks Front init complete — call from FrontChat.tsx onInitCompleted only. */
export function markFrontChatInitCompleted(): void {
  frontChatInitCompleted = true;
  const waiters = frontChatInitWaiters.splice(0, frontChatInitWaiters.length);
  for (const waiter of waiters) {
    waiter();
  }
}

/** Resets init tracking after shutdown or before a fresh init. */
export function resetFrontChatInitState(): void {
  frontChatInitCompleted = false;
  frontChatInitWaiters.length = 0;
}

export function isFrontChatInitialized(): boolean {
  return frontChatInitCompleted;
}

function runWhenFrontChatInitialized(run: () => void): void {
  if (typeof window === "undefined") {
    return;
  }

  if (window.FrontChat && frontChatInitCompleted) {
    run();
    return;
  }

  if (window.FrontChat && !frontChatInitCompleted) {
    frontChatInitWaiters.push(run);
    return;
  }

  let attempts = 0;

  const tryRun = () => {
    attempts += 1;
    if (window.FrontChat && frontChatInitCompleted) {
      run();
      return;
    }
    if (window.FrontChat && !frontChatInitCompleted) {
      frontChatInitWaiters.push(run);
      return;
    }
    if (attempts < FRONT_CHAT_SHOW_MAX_ATTEMPTS) {
      window.setTimeout(tryRun, FRONT_CHAT_SHOW_RETRY_MS);
    }
  };

  tryRun();
}

/** Shuts down Front only after init completed, avoiding StrictMode teardown errors. */
export function safeFrontChatShutdown(): void {
  if (typeof window === "undefined" || !window.FrontChat || !frontChatInitCompleted) {
    resetFrontChatInitState();
    return;
  }

  try {
    window.FrontChat("shutdown");
  } catch (error) {
    if (import.meta.env.DEV) {
      console.debug("[FrontChat] shutdown failed during teardown", error);
    }
  } finally {
    resetFrontChatInitState();
  }
}

function runFrontChatCommand(command: "show" | "hide"): void {
  try {
    window.FrontChat(command);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.debug(`[FrontChat] ${command} failed`, error);
    }
  }
}

/** Contact custom field in Front; must match Front admin field name exactly. */
export const FRONT_CHAT_ESCALATION_SOURCE_FIELD = "Escalation Source";

/** Default launcher path — Front chatbot should route to the AI flow. */
export const FRONT_CHAT_ESCALATION_SOURCE_DEFAULT = "default";

/** Scout representative CTA — Front chatbot should route to a human agent. */
export const FRONT_CHAT_ESCALATION_SOURCE_SCOUT = "scout";

export type FrontChatEscalationSource =
  | typeof FRONT_CHAT_ESCALATION_SOURCE_DEFAULT
  | typeof FRONT_CHAT_ESCALATION_SOURCE_SCOUT;

export type OpenFrontChatOptions = {
  escalationSource?: FrontChatEscalationSource;
};

/** Updates the Front contact custom field used for chatbot branching. */
export function applyFrontChatEscalationSource(escalationSource: FrontChatEscalationSource): void {
  if (typeof window === "undefined" || !window.FrontChat) {
    return;
  }

  try {
    window.FrontChat("identity", {
      customFields: {
        [FRONT_CHAT_ESCALATION_SOURCE_FIELD]: escalationSource,
      },
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.debug("[FrontChat] identity failed", error);
    }
  }
}

/** Resets Scout routing so the default Front launcher uses the AI bot path. */
export function resetFrontChatEscalationSource(): void {
  applyFrontChatEscalationSource(FRONT_CHAT_ESCALATION_SOURCE_DEFAULT);
}

/**
 * Opens the Front support chat panel. Retries briefly because Front's `show`
 * command can no-op if called before init completes (see FrontChat.tsx).
 */
export function openFrontChat(options?: OpenFrontChatOptions): void {
  runWhenFrontChatInitialized(() => {
    if (options?.escalationSource) {
      applyFrontChatEscalationSource(options.escalationSource);
    }
    runFrontChatCommand("show");
  });
}

/**
 * Opens Front for Scout escalations. Sets `escalationSource=scout` on the
 * verified contact via `identity` immediately before `show`.
 */
export function openFrontChatWithAiBotBypass(): void {
  runWhenFrontChatInitialized(() => {
    applyFrontChatEscalationSource(FRONT_CHAT_ESCALATION_SOURCE_SCOUT);
    runFrontChatCommand("show");
  });
}

/** Closes the Front support chat panel (launcher remains when enabled). */
export function hideFrontChat(): void {
  runWhenFrontChatInitialized(() => {
    runFrontChatCommand("hide");
  });
}

export type FrontChatVisibilityState = {
  is_window_visible?: number;
};

/**
 * Registers a Front visibility listener after init. Returns cleanup when the
 * SDK supports `onWindowVisibilityChanged`.
 *
 * `onClose` only runs when the window transitions from open to closed, not on
 * the initial hidden state Front sends when subscribing.
 */
export function subscribeFrontChatVisibility(
  handler: (state: FrontChatVisibilityState) => void,
  onClose?: () => void
): () => void {
  if (typeof window === "undefined" || !window.FrontChat) {
    return () => undefined;
  }

  let wasVisible = false;

  const unbind = window.FrontChat(
    "onWindowVisibilityChanged",
    (state: FrontChatVisibilityState) => {
      const visible = Boolean(state.is_window_visible);
      if (visible) {
        wasVisible = true;
      } else if (wasVisible) {
        wasVisible = false;
        onClose?.();
      }
      handler(state);
    }
  );

  return typeof unbind === "function" ? unbind : () => undefined;
}
